const minHeight = {
    name: "min-height",
    path: "/min-height",
    title: "Min Height",
    description: "Utilities for setting the minimum height of elements.",
    value: {
        "min-height": {
            "min-h-<number>": "calc(var(--1) * <number>)",
            "min-h-<fraction>": "calc(<fraction> * 100%)",
            'min-h-3xs': 'var(--container-3xs)',
            'min-h-2xs': 'var(--container-2xs)',
            'min-h-xs': 'var(--container-xs)',
            'min-h-sm': 'var(--container-sm)',
            'min-h-md': 'var(--container-md)',
            'min-h-lg': 'var(--container-lg)',
            'min-h-xl': 'var(--container-xl)',
            'min-h-2xl': 'var(--container-2xl)',
            'min-h-3xl': 'var(--container-3xl)',
            'min-h-4xl': 'var(--container-4xl)',
            'min-h-5xl': 'var(--container-5xl)',
            'min-h-6xl': 'var(--container-6xl)',
            'min-h-7xl': 'var(--container-7xl)',
            'min-h-auto': 'auto',
            'min-h-full': '100%',
            'min-h-min': 'min-content',
            'min-h-max': 'max-content',
            'min-h-fit': 'fit-content',
            'min-h-screen': '100vh',
            'min-h-dvh': '100dvh',
            'min-h-lvh': '100lvh',
            'min-h-svh': '100svh',
            'min-h-dvw': '100dvw',
            'min-h-lvw': '100lvw',
            'min-h-svw': '100svw',
            'max-h-px': '1px',
            "min-h-[<value>]": "<value>",
        }
    },
exampleData: [
        {
            "example": {
                "code": `
<div class="min-h-96 bg-color-blue-500 rounded-lg p-2 text-center">min-h-96</div>
<div class="min-h-80 bg-color-blue-500 rounded-lg p-2 text-center">min-h-80</div>
<div class="min-h-64 bg-color-blue-500 rounded-lg p-2 text-center">min-h-64</div>
<div class="min-h-48 bg-color-blue-500 rounded-lg p-2 text-center">min-h-48</div>
<div class="min-h-40 bg-color-blue-500 rounded-lg p-2 text-center">min-h-40</div>
<div class="min-h-32 bg-color-blue-500 rounded-lg p-2 text-center">min-h-32</div>
<div class="min-h-24 bg-color-blue-500 rounded-lg p-2 text-center">min-h-24</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="flex flex-col min-h-64 ...">
  <div class="min-h-1/2 ...">min-h-1/2</div>
  <div class="min-h-1/2 ...">min-h-1/2</div>
</div>
<div class="flex flex-col min-h-64 ...">
  <div class="min-h-2/5 ...">min-h-2/5</div>
  <div class="min-h-3/5 ...">min-h-3/5</div>
</div>
<div class="flex flex-col min-h-64 ...">
  <div class="min-h-1/3 ...">min-h-1/3</div>
  <div class="min-h-2/3 ...">min-h-2/3</div>
</div>
<div class="flex flex-col min-h-64 ...">
  <div class="min-h-1/4 ...">min-h-1/4</div>
  <div class="min-h-3/4 ...">min-h-3/4</div>
</div>
<div class="flex flex-col min-h-64 ...">
  <div class="min-h-1/5 ...">min-h-1/5</div>
  <div class="min-h-4/5 ...">min-h-4/5</div>
</div>
<div class="flex flex-col min-h-64 ...">
  <div class="min-h-1/6 ...">min-h-1/6</div>
  <div class="min-h-5/6 ...">min-h-5/6</div>
</div>
<div class="min-h-full ...">min-h-full</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="min-h-xl ...">min-h-xl</div>
<div class="min-h-lg ...">min-h-lg</div>
<div class="min-h-md ...">min-h-md</div>
<div class="min-h-sm ...">min-h-sm</div>
<div class="min-h-xs ...">min-h-xs</div>
<div class="min-h-2xs ...">min-h-2xs</div>
<div class="min-h-[200px] ...">min-h-[200px]</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="size-[64px] ...">size-16</div>
<div class="size-20 ...">size-20</div>
<div class="size-24 ...">size-24</div>
<div class="size-32 ...">size-32</div>
<div class="size-40 ...">size-40</div>
                `
            }
        }
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">min-h-&lt;number&gt;</code> utilities to set fixed height values:
            </p>
            <div class="flex gap-4 justify-center items-end text-color-white font-mono font-bold relative">
                <div class="absolute inset-x-0 z-0 h-20">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="min-h-96 bg-color-blue-500 rounded-lg text-center flex items-end relative">
                    <div class="mb-10 -rotate-90 text-left text-nowrap">min-h-96</div>
                </div>
                <div class="min-h-64 bg-color-blue-500 rounded-lg text-center flex items-end relative">
                    <div class="mb-10 -rotate-90 text-left text-nowrap">min-h-64</div>
                </div>
                <div class="min-h-48 bg-color-blue-500 rounded-lg text-center flex items-end relative">
                    <div class="mb-10 -rotate-90 text-left text-nowrap">min-h-48</div>
                </div>
                <div class="min-h-40 bg-color-blue-500 rounded-lg text-center flex items-end relative">
                    <div class="mb-10 -rotate-90 text-left text-nowrap">min-h-40</div>
                </div>
                <div class="min-h-32 bg-color-blue-500 rounded-lg text-center flex items-end relative">
                    <div class="mb-10 -rotate-90 text-left text-nowrap">min-h-32</div>
                </div>
                <div class="min-h-24 bg-color-blue-500 rounded-lg text-center flex items-end relative">
                    <div class="mb-10 -rotate-90 text-left text-nowrap">min-h-24</div>
                </div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">min-h-full, min-h-&lt;fraction&gt;</code>
                utilities like min-h-1/2 and min-h-2/5 to give an element a percentage-based height:
            </p>
            <div class="flex gap-4 justify-center items-end text-color-white font-mono font-bold h-96">
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="min-h-full bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-12 -rotate-90 text-left text-nowrap">min-h-full</div>
                    </div>
                </div>
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="min-h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="min-h-9/10 bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-12 -rotate-90 text-left text-nowrap">min-h-9/10</div>
                    </div>
                </div>
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="min-h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="min-h-3/4 bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-12 -rotate-90 text-left text-nowrap">min-h-3/4</div>
                    </div>
                </div>
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="min-h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="min-h-1/2 bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-12 -rotate-90 text-left text-nowrap">min-h-1/2</div>
                    </div>
                </div>
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="min-h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="min-h-1/3 bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-12 -rotate-90 text-left text-nowrap">min-h-1/3</div>
                    </div>
                </div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                <code class="text-color-gray-800 dark:text-color-white font-semibold">Using the container scale</code>
                Use utilities like min-h-sm and min-h-xl to set an element to a fixed height based on the container scale:
            </p>
            <div class="flex gap-4 justify-center items-end text-color-white font-mono font-bold">
                <div class="min-h-xl bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-12 -rotate-90 text-left text-nowrap">min-h-xl</div>
                </div>
                <div class="min-h-lg bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-12 -rotate-90 text-left text-nowrap">min-h-lg</div>
                </div>
                <div class="min-h-md bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-12 -rotate-90 text-left text-nowrap">min-h-md</div>
                </div>
                <div class="min-h-sm bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-12 -rotate-90 text-left text-nowrap">min-h-sm</div>
                </div>
                <div class="min-h-xs bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-12 -rotate-90 text-left text-nowrap">min-h-xs</div>
                </div>
                <div class="min-h-2xs bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-12 -rotate-90 text-left text-nowrap">min-h-2xs</div>
                </div>
                <div class="min-h-[200px] bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8">
                    <div class="mb-16 -rotate-90 text-left text-nowrap">min-h-[200px]</div>
                </div>
            </div>
            `
        }
    ]
};

export default minHeight;

<div className="sr-only h-20 mb-12  mb-16 space-y-4 min-h-[200px] min-h-96 min-h-80 min-h-64 min-h-48 min-h-40 min-h-32 min-h-24 w-8 -rotate-90 text-left text-nowrap min-h-full min-h-9/10 min-h-3/4 min-h-1/2 min-h-1/3 min-h-xl min-h-lg min-h-md min-h-sm min-h-xs min-h-2xs min-h-3xs">

</div>